import React, { useEffect, useMemo } from 'react';

// Default values (moved outside component since it's a constant)
const defaultGradientData = {
  ctSecondaryCurrent: { initial: '0', g1: '5', g2: '', g3: '' },
  overload120Percent: { initial: '-', g1: '-', g2: '6', g3: '' },
  maxContinuousCurrent: { initial: '-', g1: '-', g2: '6', g3: '' },
  reqTempRise: { initial: '0', g1: '15', g2: '', g3: '' },
  wtiReading: { initial: '60', g1: '75', g2: '', g3: '' },
};

const WindingTemperatureGradientReading = ({ formData, updateFormData, nextStep, prevStep, currentStep, totalSteps }) => {

  // Get current in Amps from Product Specifications
  const currentInAmps = formData.currentInAmps || '5';
  
  // Get Winding Temperature from Product Specifications
  const windingTemperature = formData.windingTemperature || '15';
  
  // Get Requested Temperature Rise from Product Specifications
  const requestedTemperatureRise = formData.requestedTemperatureRise || '2';
  
  // Calculate 120% of current in Amps
  const current120Percent = useMemo(() => {
    const current = parseFloat(currentInAmps) || 5;
    const result = (current * 1.2).toFixed(1);
    // Remove trailing .0 if needed
    return result.endsWith('.0') ? result.slice(0, -2) : result;
  }, [currentInAmps]);

  // Use formData values or defaults
  const gradientData = formData.windingTempGradient || defaultGradientData;

  // Calculate G1 for WTI reading: REQ. Temp. Rise G1 + WTI reading Initial
  const wtiReadingG1 = useMemo(() => {
    const reqTempRiseG1 = parseFloat(windingTemperature) || 15;
    const wtiInitial = parseFloat(gradientData.wtiReading?.initial) || 60;
    const sum = reqTempRiseG1 + wtiInitial;
    // Return as string, remove .0 if it's a whole number
    return sum % 1 === 0 ? sum.toString() : sum.toFixed(1);
  }, [windingTemperature, gradientData.wtiReading?.initial]);

  // Calculate G2 for WTI reading: WTI reading Initial + REQ. Temp. Rise G2
  const wtiReadingG2 = useMemo(() => {
    const reqTempRiseG2 = parseFloat(requestedTemperatureRise) || 2;
    const wtiInitial = parseFloat(gradientData.wtiReading?.initial) || 60;
    const sum = reqTempRiseG2 + wtiInitial;
    // Return as string, remove .0 if it's a whole number
    return sum % 1 === 0 ? sum.toString() : sum.toFixed(1);
  }, [requestedTemperatureRise, gradientData.wtiReading?.initial]);

  // Initialize default values in formData if not present
  useEffect(() => {
    if (!formData.windingTempGradient) {
      updateFormData({ windingTempGradient: defaultGradientData });
    }
  }, [formData.windingTempGradient, updateFormData]);

  // Auto-update values based on currentInAmps from Product Specifications
  useEffect(() => {
    if (formData.windingTempGradient) {
      const updatedData = { ...formData.windingTempGradient };
      let hasChanges = false;

      // Update G1 in CT Secondary current in Amps
      if (updatedData.ctSecondaryCurrent.g1 !== currentInAmps) {
        updatedData.ctSecondaryCurrent = {
          ...updatedData.ctSecondaryCurrent,
          g1: currentInAmps,
        };
        hasChanges = true;
      }

      // Update G2 in 120% overload Current in Amps
      if (updatedData.overload120Percent.g2 !== current120Percent) {
        updatedData.overload120Percent = {
          ...updatedData.overload120Percent,
          g2: current120Percent,
        };
        hasChanges = true;
      }

      // Update G2 in Maximum continuous current allowed in Amps
      if (updatedData.maxContinuousCurrent.g2 !== current120Percent) {
        updatedData.maxContinuousCurrent = {
          ...updatedData.maxContinuousCurrent,
          g2: current120Percent,
        };
        hasChanges = true;
      }

      // Update G1 in REQ. Temp. Rise over Top oil in °C
      if (updatedData.reqTempRise.g1 !== windingTemperature) {
        updatedData.reqTempRise = {
          ...updatedData.reqTempRise,
          g1: windingTemperature,
        };
        hasChanges = true;
      }

      // Update G2 in REQ. Temp. Rise over Top oil in °C
      if (updatedData.reqTempRise.g2 !== requestedTemperatureRise) {
        updatedData.reqTempRise = {
          ...updatedData.reqTempRise,
          g2: requestedTemperatureRise,
        };
        hasChanges = true;
      }

      // Update G1 in WTI reading (sum of REQ. Temp. Rise G1 + WTI reading Initial)
      const reqTempRiseG1 = parseFloat(windingTemperature) || 15;
      const wtiInitial = parseFloat(updatedData.wtiReading?.initial) || 60;
      const wtiG1Sum = reqTempRiseG1 + wtiInitial;
      const wtiG1Value = wtiG1Sum % 1 === 0 ? wtiG1Sum.toString() : wtiG1Sum.toFixed(1);
      
      if (updatedData.wtiReading.g1 !== wtiG1Value) {
        updatedData.wtiReading = {
          ...updatedData.wtiReading,
          g1: wtiG1Value,
        };
        hasChanges = true;
      }

      // Update G2 in WTI reading (sum of WTI reading Initial + REQ. Temp. Rise G2)
      const reqTempRiseG2 = parseFloat(requestedTemperatureRise) || 2;
      const wtiG2Sum = reqTempRiseG2 + wtiInitial;
      const wtiG2Value = wtiG2Sum % 1 === 0 ? wtiG2Sum.toString() : wtiG2Sum.toFixed(1);
      
      if (updatedData.wtiReading.g2 !== wtiG2Value) {
        updatedData.wtiReading = {
          ...updatedData.wtiReading,
          g2: wtiG2Value,
        };
        hasChanges = true;
      }

      if (hasChanges) {
        updateFormData({ windingTempGradient: updatedData });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInAmps, current120Percent, windingTemperature, requestedTemperatureRise, gradientData.wtiReading?.initial]);

  const handleInputChange = (rowKey, columnKey, value) => {
    const updatedData = {
      ...gradientData,
      [rowKey]: {
        ...gradientData[rowKey],
        [columnKey]: value,
      },
    };
    updateFormData({ windingTempGradient: updatedData });
  };

  return (
    <div className="form-section">
      <h3>Winding Temperature Gradient Reading</h3>
      
      <div className="table-container">
        <table className="test-table">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>No.</th>
              <th>Gradient</th>
              <th>Initial</th>
              <th>G1</th>
              <th>G2</th>
              <th>G3</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ textAlign: 'center', fontWeight: 'bold' }}>1</td>
              <td>CT Secondary current in Amps</td>
              <td>
                <input
                  type="text"
                  value={gradientData.ctSecondaryCurrent.initial}
                  onChange={(e) => handleInputChange('ctSecondaryCurrent', 'initial', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={currentInAmps}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  title="Auto-populated from Product Specifications - Current in Amps"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={gradientData.ctSecondaryCurrent.g2}
                  onChange={(e) => handleInputChange('ctSecondaryCurrent', 'g2', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={gradientData.ctSecondaryCurrent.g3}
                  onChange={(e) => handleInputChange('ctSecondaryCurrent', 'g3', e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'center', fontWeight: 'bold' }}>2</td>
              <td>120% overload Current in Amps</td>
              <td>
                <input
                  type="text"
                  value={gradientData.overload120Percent.initial}
                  onChange={(e) => handleInputChange('overload120Percent', 'initial', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={gradientData.overload120Percent.g1}
                  onChange={(e) => handleInputChange('overload120Percent', 'g1', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={current120Percent}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  title="Auto-calculated as 120% of Current in Amps from Product Specifications"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={gradientData.overload120Percent.g3}
                  onChange={(e) => handleInputChange('overload120Percent', 'g3', e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'center', fontWeight: 'bold' }}>3</td>
              <td>Maximum continuous current allowed in Amps</td>
              <td>
                <input
                  type="text"
                  value={gradientData.maxContinuousCurrent.initial}
                  onChange={(e) => handleInputChange('maxContinuousCurrent', 'initial', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={gradientData.maxContinuousCurrent.g1}
                  onChange={(e) => handleInputChange('maxContinuousCurrent', 'g1', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={current120Percent}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  title="Auto-calculated as 120% of Current in Amps from Product Specifications"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={gradientData.maxContinuousCurrent.g3}
                  onChange={(e) => handleInputChange('maxContinuousCurrent', 'g3', e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'center', fontWeight: 'bold' }}>4</td>
              <td>REQ. Temp. Rise over Top oil in °C</td>
              <td>
                <input
                  type="text"
                  value={gradientData.reqTempRise.initial}
                  onChange={(e) => handleInputChange('reqTempRise', 'initial', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={windingTemperature}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  title="Auto-populated from Product Specifications - Winding Temperature in ℃"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={requestedTemperatureRise}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  title="Auto-populated from Product Specifications - Requested Temperature Rise over Top oil in ℃"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={gradientData.reqTempRise.g3}
                  onChange={(e) => handleInputChange('reqTempRise', 'g3', e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'center', fontWeight: 'bold' }}>5</td>
              <td>WTI reading</td>
              <td>
                <input
                  type="text"
                  value={gradientData.wtiReading.initial}
                  onChange={(e) => handleInputChange('wtiReading', 'initial', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={wtiReadingG1}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  title="Auto-calculated as sum of (REQ. Temp. Rise G1 + WTI reading Initial)"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={wtiReadingG2}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  title="Auto-calculated as sum of (WTI reading Initial + REQ. Temp. Rise G2)"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={gradientData.wtiReading.g3}
                  onChange={(e) => handleInputChange('wtiReading', 'g3', e.target.value)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div className="navigation-buttons">
        <button 
          className="btn btn-secondary" 
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          Previous
        </button>
        <button 
          className="btn btn-primary" 
          onClick={nextStep}
          disabled={currentStep === totalSteps}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default WindingTemperatureGradientReading;

